import { observer } from "mobx-react";
import React, { useContext } from "react";
import {
  Paper,
  Stack,
  NumberInput,
  NumberInputProps,
  Text,
  Group,
  Box,
  Badge,
  CopyButton,
  ActionIcon,
  Tooltip,
} from "@mantine/core";
import {
  IconArrowsExchange,
  IconCurrencyBitcoin,
  IconCheck,
  IconCopy,
} from "@tabler/icons-react";
import { SatoshiConverterStoreContext } from "../stores/SatoshiConverterStore";
import { t } from "../helpers/translations";
import { formatNumberString } from "../helpers/numberFormat";

const ConverterWidget: React.FC = observer(() => {
  const store = useContext(SatoshiConverterStoreContext);
  const s = store.strings;
  const code = store.currencyCode.toUpperCase();
  const separators = store.numberSeparators;
  // Both boxes group digits in the page's language as you type. The store keeps the
  // plain value ("80596000.00"); NumericFormat strips the separators again on input.
  const amountInputProps: NumberInputProps = {
    thousandSeparator: separators.group,
    decimalSeparator: separators.decimal,
    thousandsGroupStyle: separators.groupStyle,
    valueIsNumericString: true,
    allowNegative: false,
    hideControls: true,
    withKeyboardEvents: false,
    autoCapitalize: "none",
    autoCorrect: "off",
    spellCheck: false,
    size: "lg",
    styles: {
      input: {
        fontFamily: "courier-prime, monospace",
        fontSize: "20px",
        backgroundColor: "rgba(0, 0, 0, 0.3)",
        border: "1px solid rgba(255, 255, 255, 0.15)",
        color: "white",
        textAlign: "right",
      },
    },
    rightSectionWidth: 50,
  };

  return (
    <Paper
      p="xl"
      radius="md"
      style={{
        backgroundColor: "rgba(0, 0, 0, 0.45)",
        border: "1px solid rgba(255, 255, 255, 0.1)",
        backdropFilter: "blur(10px)",
      }}
    >
      <Stack gap="lg">
        {/* Sats input */}
        <Box>
          <Text size="sm" c="dimmed" mb={4} fw={500}>
            {s.satoshisLabel}
          </Text>
          <NumberInput
            {...amountInputProps}
            placeholder={s.enterSatoshis}
            allowDecimal={false}
            inputMode="numeric"
            value={store.satsInput}
            onValueChange={({ value }, { source }) => {
              // "prop" = the box reformatting a value the store computed
              if (source === "event") store.setSatsInput(value);
            }}
            rightSection={
              <Text size="sm" c="dimmed" mr="xs">
                sats
              </Text>
            }
          />
        </Box>

        {/* Exchange arrows */}
        <Group justify="center">
          <IconArrowsExchange
            size={28}
            color="rgba(255,255,255,0.4)"
            style={{ transform: "rotate(90deg)" }}
          />
        </Group>

        {/* Fiat input */}
        <Box>
          <Text size="sm" c="dimmed" mb={4} fw={500}>
            {store.currencyName} ({code})
          </Text>
          <NumberInput
            {...amountInputProps}
            placeholder={t(s.enterAmountIn, { code })}
            inputMode="decimal"
            value={store.fiatInput}
            onValueChange={({ value }, { source }) => {
              if (source === "event") store.setFiatInput(value);
            }}
            rightSection={
              <Text size="sm" c="dimmed" mr="xs">
                {store.currencySymbol}
              </Text>
            }
          />
        </Box>

        {/* Conversion result with copy button */}
        {store.satsInput && parseInt(store.satsInput, 10) > 0 && (
          <Paper
            p="md"
            radius="sm"
            style={{
              backgroundColor: "rgba(255, 165, 0, 0.08)",
              border: "1px solid rgba(255, 165, 0, 0.2)",
            }}
          >
            <CopyButton
              value={
                store.lastEdited === "fiat" ? store.satsInput : store.fiatInput
              }
            >
              {({ copied, copy }) => (
                <Tooltip
                  label={copied ? "Copied!" : "Copy to clipboard"}
                  withArrow
                  position="top"
                >
                  <Box
                    onClick={copy}
                    style={{
                      cursor: "pointer",
                      borderRadius: "8px",
                      padding: "8px 12px",
                      transition: "background-color 150ms ease",
                      backgroundColor: copied
                        ? "rgba(32, 201, 151, 0.1)"
                        : "transparent",
                    }}
                  >
                    <Group justify="center" gap={6} align="baseline">
                      <Text
                        size="xs"
                        fw={400}
                        style={{
                          fontFamily: "courier-prime, monospace",
                          color: "rgba(255, 255, 255, 0.35)",
                          transition: "color 150ms ease",
                        }}
                      >
                        {store.lastEdited === "fiat"
                          ? `${store.formattedFiat} ${code} =`
                          : `${store.formattedSats} ${store.satsUnit} =`}
                      </Text>
                      <Text
                        size="xl"
                        fw={700}
                        style={{
                          fontFamily: "courier-prime, monospace",
                          color: copied ? "#20c997" : "#f7931a",
                          fontSize: "24px",
                          transition: "color 150ms ease",
                        }}
                      >
                        {store.lastEdited === "fiat"
                          ? `${store.formattedSats} ${store.satsUnit}`
                          : `${store.formattedFiat} ${code}`}
                      </Text>
                      <ActionIcon
                        variant={copied ? "filled" : "subtle"}
                        color={copied ? "teal" : "orange"}
                        size="lg"
                        radius="xl"
                        style={{
                          transition: "all 150ms ease",
                        }}
                      >
                        {copied ? (
                          <IconCheck size={18} />
                        ) : (
                          <IconCopy size={18} />
                        )}
                      </ActionIcon>
                    </Group>
                  </Box>
                </Tooltip>
              )}
            </CopyButton>

            {/* BTC equivalent — secondary info with copy */}
            <Group justify="center" gap={6} mt="xs">
              <IconCurrencyBitcoin size={16} color="rgba(247, 147, 26, 0.6)" />
              <Text
                size="xs"
                c="dimmed"
                style={{ fontFamily: "courier-prime, monospace" }}
              >
                {formatNumberString(store.btcAmount, separators)} BTC
              </Text>
              <CopyButton value={store.btcAmount}>
                {({ copied, copy }) => (
                  <Tooltip
                    label={copied ? "Copied!" : "Copy BTC"}
                    withArrow
                    position="right"
                  >
                    <ActionIcon
                      variant="transparent"
                      color={copied ? "teal" : "gray"}
                      size="xs"
                      onClick={copy}
                    >
                      {copied ? (
                        <IconCheck size={12} />
                      ) : (
                        <IconCopy size={12} />
                      )}
                    </ActionIcon>
                  </Tooltip>
                )}
              </CopyButton>
            </Group>
          </Paper>
        )}

        {/* Live rate badge */}
        <Group justify="center">
          <Badge
            variant="dot"
            color="teal"
            size="sm"
            style={{ backgroundColor: "rgba(0,0,0,0.3)" }}
          >
            1 BTC = {store.formattedBtcPrice} {code}
          </Badge>
        </Group>
      </Stack>
    </Paper>
  );
});

export default ConverterWidget;
